import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Button from '../components/ui/button/Button';
import { 
  MapPin, 
  Package, 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  CreditCard,
  ArrowRight,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PageMeta from '../components/common/PageMeta';
import bulletService from '../services/bulletService';
import appFetch from '../utils/appFetch';
import rateService from '../services/rateService';

interface Country {
  code: string;
  name: string;
  shortName: string;
  phoneCode: string;
}

interface TimeSlot {
  id: string;
  time: string;
  description: string;
}

interface BookingData {
  // Customer Information
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  
  // Pickup Information
  pickupCountry: string;
  pickupCity: string;
  pickupAddress: string;
  pickupPostalCode: string;
  pickupContactName: string;
  pickupContactPhone: string;
  
  // Drop-off Information
  dropoffCountry: string;
  dropoffCity: string;
  dropoffAddress: string;
  dropoffPostalCode: string;
  dropoffContactName: string;
  dropoffContactPhone: string;
  
  // Package Information
  packageType: 'PACKAGE' | 'DOCUMENT';
  weight: number;
  weightUnit: 'KG';
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit: 'CM';
  packageDescription: string;
  
  // Pickup Schedule
  pickupDate: Date | null;
  timeSlot: string;
  
  // Delivery Schedule
  deliveryDate: Date | null;
  deliveryTimeSlot: string;
  
  // Service Information
  serviceType: string;
}

// Custom styles for react-datepicker
const datePickerStyles = `
  .react-datepicker-popper {
    z-index: 9999 !important;
  }
  .react-datepicker {
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    font-family: inherit;
  }
  .react-datepicker__header {
    background-color: #f9fafb;
    border-bottom: 1px solid #d1d5db;
    border-radius: 0.5rem 0.5rem 0 0;
  }
  .react-datepicker__current-month {
    color: #374151;
    font-weight: 600;
  }
  .react-datepicker__day-name {
    color: #6b7280;
    font-weight: 500;
  }
  .react-datepicker__day--selected {
    background-color: #2563eb;
    color: white;
  }
  .react-datepicker__day--selected:hover {
    background-color: #1d4ed8;
  }
  .react-datepicker__day:hover {
    background-color: #e5e7eb;
  }
  .react-datepicker__day--disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
  .react-datepicker__day--disabled:hover {
    background-color: transparent;
  }
`;

const BookDelivery: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [countries, setCountries] = useState<Country[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [deliveryTimeSlots, setDeliveryTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState({
    total: 0
  });
  
  // Phone number validation errors
  const [phoneErrors, setPhoneErrors] = useState({
    customerPhone: '',
    pickupContactPhone: '',
    dropoffContactPhone: ''
  });

  const [bookingData, setBookingData] = useState<BookingData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    pickupCountry: '',
    pickupCity: '',
    pickupAddress: '',
    pickupPostalCode: '',
    pickupContactName: '',
    pickupContactPhone: '',
    dropoffCountry: '',
    dropoffCity: '',
    dropoffAddress: '',
    dropoffPostalCode: '',
    dropoffContactName: '',
    dropoffContactPhone: '',
    packageType: 'PACKAGE',
    weight: 0,
    weightUnit: 'KG',
    length: 0,
    width: 0,
    height: 0,
    dimensionUnit: 'CM',
    packageDescription: '',
    pickupDate: null,
    timeSlot: '',
    deliveryDate: null,
    deliveryTimeSlot: '',
    serviceType: 'STANDARD'
  });

  useEffect(() => {
    fetchCountries();
    // Initial calculation with default values
    calculateEstimatedCost();
  }, []);

  useEffect(() => {
    if (bookingData.pickupDate) {
      const dateString = formatDateToString(bookingData.pickupDate);
      fetchTimeSlots(dateString);
    }
  }, [bookingData.pickupDate]);

  // Helper function to check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false;
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  useEffect(() => {
    calculateEstimatedCost();
  }, [bookingData.weight, bookingData.pickupCountry, bookingData.dropoffCountry, bookingData.packageType, bookingData.length, bookingData.width, bookingData.height]);

  // Automatically set dropoff country to match pickup country (Bullet Delivery is domestic only)
  useEffect(() => {
    if (bookingData.pickupCountry && bookingData.pickupCountry !== bookingData.dropoffCountry) {
      setBookingData(prev => ({
        ...prev,
        dropoffCountry: bookingData.pickupCountry
      }));
    }
  }, [bookingData.pickupCountry]);

  // Fetch delivery time slots when pickup slot, pickup date, and delivery date are selected
  useEffect(() => {
    const fetchDeliverySlots = async () => {
      if (bookingData.timeSlot && bookingData.pickupDate && bookingData.deliveryDate) {
        try {
          const pickupDateStr = formatDateToString(bookingData.pickupDate);
          const deliveryDateStr = formatDateToString(bookingData.deliveryDate);
          const slots = await bulletService.getDeliveryTimeSlots(bookingData.timeSlot, pickupDateStr, deliveryDateStr);
          setDeliveryTimeSlots(slots);
        } catch (error) {
          console.error('Error fetching delivery slots:', error);
        }
      }
    };
    
    fetchDeliverySlots();
  }, [bookingData.timeSlot, bookingData.pickupDate, bookingData.deliveryDate]);

  // Helper function to format date without timezone conversion
  const formatDateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  // Get minimum date (today)
  const getMinDate = (): Date => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const fetchCountries = async () => {
    try {
      const countries = await bulletService.getSupportedCountries();
      setCountries(countries);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };


  const fetchTimeSlots = async (pickupDate: string) => {
    try {
      const timeSlots = await bulletService.getTimeSlots(pickupDate);
      setTimeSlots(timeSlots);
    } catch (error) {
      console.error('Error fetching time slots:', error);
    }
  };

  const calculateEstimatedCost = async () => {
    // Only calculate if we have required data
    if (!bookingData.pickupCountry || !bookingData.dropoffCountry || !bookingData.weight || bookingData.weight <= 0) {
      setPriceBreakdown({
        total: 0
      });
      return;
    }

    setCalculatingPrice(true);

    try {
      // For DOCUMENT: only use weight
      // For PACKAGE: use weight and dimensions (length, width, height)
      let ratePayload;
      let finalWeight = bookingData.weight;
      
      if (bookingData.packageType === 'DOCUMENT') {
        // Document type - only weight is required
        ratePayload = {
          origin: bookingData.pickupCountry,
          destination: bookingData.dropoffCountry,
          weight: bookingData.weight,
          length: 1,
          breadth: 1,
          height: 1,
          pcs: 1
        };
      } else {
        // Package type - calculate dimensional weight and use the larger value
        const length = bookingData.length || 1;
        const width = bookingData.width || 1;
        const height = bookingData.height || 1;
        
        // Calculate dimensional weight (volumetric weight)
        // Formula: (Length × Width × Height) / 5000 for cm to kg
        const dimensionalWeight = (length * width * height) / 5000;
        
        // Use the larger weight (actual weight vs dimensional weight)
        finalWeight = Math.max(bookingData.weight, dimensionalWeight);
        
        console.log('Weight Calculation:', {
          actualWeight: bookingData.weight,
          dimensionalWeight: dimensionalWeight.toFixed(2),
          finalWeight: finalWeight.toFixed(2)
        });
        
        ratePayload = {
          origin: bookingData.pickupCountry,
          destination: bookingData.dropoffCountry,
          weight: finalWeight,
          length: length,
          breadth: width,
          height: height,
          pcs: 1
        };
      }

      const rateResponse = await rateService.calculateRate(ratePayload);

      if (rateResponse && rateResponse.success && rateResponse.rate) {
        const total = parseFloat(rateResponse.rate);
        
        // Update price with API response
        setPriceBreakdown({
          total: Math.round(total * 100) / 100
        });
      } else {
        // Fallback to simple calculation if API fails
        const fallbackTotal = 10 + (finalWeight * 2);
        setPriceBreakdown({
          total: fallbackTotal
        });
        console.warn('Rate calculation failed, using fallback pricing. Error:', rateResponse?.error || 'Unknown error');
      }
    } catch (error) {
      // Fallback to simple calculation on error
      const fallbackTotal = 10 + (bookingData.weight * 2);
      setPriceBreakdown({
        total: fallbackTotal
      });
      console.error('Error calculating rate:', error);
    } finally {
      setCalculatingPrice(false);
    }
  };

  // Validate phone number
  const validatePhoneNumber = (phone: string): string => {
    if (!phone) {
      return '';
    }
    
    // Remove all spaces, dashes, parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
    
    // Check if it contains only digits and optional + at the start
    const phoneRegex = /^\+?\d+$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number can only contain digits, spaces, dashes, parentheses, and + symbol';
    }
    
    // Check minimum length (at least 10 digits)
    const digitsOnly = cleanedPhone.replace(/\+/g, '');
    if (digitsOnly.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    
    // Check maximum length (max 15 digits for international numbers)
    if (digitsOnly.length > 15) {
      return 'Phone number cannot exceed 15 digits';
    }
    
    return '';
  };

  const handleInputChange = (field: keyof BookingData, value: any) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validate phone numbers on change
    if (field === 'customerPhone' || field === 'pickupContactPhone' || field === 'dropoffContactPhone') {
      const error = validatePhoneNumber(value);
      setPhoneErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        // Check required fields
        const step1Valid = !!(bookingData.pickupCountry && bookingData.pickupCity && bookingData.pickupAddress &&
                 bookingData.dropoffCountry && bookingData.dropoffCity && bookingData.dropoffAddress);
        
        // If phone numbers are provided, they must be valid
        if (bookingData.pickupContactPhone && phoneErrors.pickupContactPhone) {
          return false;
        }
        if (bookingData.dropoffContactPhone && phoneErrors.dropoffContactPhone) {
          return false;
        }
        
        return step1Valid;
      case 2:
        return !!(bookingData.packageType && bookingData.weight > 0 && bookingData.packageDescription);
      case 3:
        // Check required fields and validate customer phone
        return !!(bookingData.customerName && bookingData.customerEmail && bookingData.customerPhone && !phoneErrors.customerPhone);
      case 4:
        return !!(bookingData.pickupDate && bookingData.timeSlot && bookingData.deliveryDate && bookingData.deliveryTimeSlot);
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Step 1: Store booking data in session storage for after payment
      const formattedBookingData = {
        ...bookingData,
        pickupDate: bookingData.pickupDate ? formatDateToString(bookingData.pickupDate) : '',
        deliveryDate: bookingData.deliveryDate ? formatDateToString(bookingData.deliveryDate) : ''
      };
      
      // Store booking data temporarily
      sessionStorage.setItem('pendingBookingData', JSON.stringify(formattedBookingData));
      
      // Step 2: Generate payment session without creating booking
      const paymentData = await appFetch('/api/generatePaymentSession', {
        method: 'POST',
        body: JSON.stringify({ 
          amount: priceBreakdown.total,
          currency: 'AED',
          customerEmail: bookingData.customerEmail,
          customerName: bookingData.customerName
        }),
      });
      
      if (paymentData._links && paymentData._links.payment && paymentData._links.payment.href) {
        // Step 3: Redirect directly to payment gateway
        window.location.href = paymentData._links.payment.href;
      } else {
        alert('Error generating payment session: ' + (paymentData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating payment session:', error);
      alert('Error processing payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4, 5].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
            step <= currentStep 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-200 text-gray-600'
          }`}>
            {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
          </div>
          {step < 5 && (
            <div className={`w-12 h-1 mx-2 ${
              step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <MapPin className="w-5 h-5" />
          <span>Pickup & Drop-off Locations</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pickup Information */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Pickup Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Country</label>
              <select
                value={bookingData.pickupCountry}
                onChange={(e) => handleInputChange('pickupCountry', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">City</label>
              <input
                type="text"
                value={bookingData.pickupCity}
                onChange={(e) => handleInputChange('pickupCity', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter city"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Address</label>
              <textarea
                value={bookingData.pickupAddress}
                onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter full pickup address"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Postal Code</label>
              <input
                type="text"
                value={bookingData.pickupPostalCode}
                onChange={(e) => handleInputChange('pickupPostalCode', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Postal code"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contact Name</label>
              <input
                type="text"
                value={bookingData.pickupContactName}
                onChange={(e) => handleInputChange('pickupContactName', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Contact person name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contact Phone</label>
              <input
                type="tel"
                value={bookingData.pickupContactPhone}
                onChange={(e) => handleInputChange('pickupContactPhone', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 ${
                  phoneErrors.pickupContactPhone 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Contact phone number (e.g., +971501234567)"
              />
              {phoneErrors.pickupContactPhone && (
                <p className="text-red-500 text-sm mt-1">{phoneErrors.pickupContactPhone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Drop-off Information */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Drop-off Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Country</label>
              <select
                value={bookingData.dropoffCountry}
                onChange={(e) => handleInputChange('dropoffCountry', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled
                required
              >
                <option value="">Same as pickup country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                🚚 Bullet Delivery operates within the same country only
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">City</label>
              <input
                type="text"
                value={bookingData.dropoffCity}
                onChange={(e) => handleInputChange('dropoffCity', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter city"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Address</label>
              <textarea
                value={bookingData.dropoffAddress}
                onChange={(e) => handleInputChange('dropoffAddress', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter full drop-off address"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Postal Code</label>
              <input
                type="text"
                value={bookingData.dropoffPostalCode}
                onChange={(e) => handleInputChange('dropoffPostalCode', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Postal code"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contact Name</label>
              <input
                type="text"
                value={bookingData.dropoffContactName}
                onChange={(e) => handleInputChange('dropoffContactName', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Contact person name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contact Phone</label>
              <input
                type="tel"
                value={bookingData.dropoffContactPhone}
                onChange={(e) => handleInputChange('dropoffContactPhone', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 ${
                  phoneErrors.dropoffContactPhone 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Contact phone number (e.g., +971501234567)"
              />
              {phoneErrors.dropoffContactPhone && (
                <p className="text-red-500 text-sm mt-1">{phoneErrors.dropoffContactPhone}</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Package className="w-5 h-5" />
          <span>Package Information</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Package Type</label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleInputChange('packageType', 'PACKAGE')}
              className={`p-4 border-2 rounded-lg text-center ${
                bookingData.packageType === 'PACKAGE'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Package className="w-8 h-8 mx-auto mb-2" />
              <div className="font-medium">Package</div>
              <div className="text-sm text-gray-600">Boxes, items, goods</div>
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('packageType', 'DOCUMENT')}
              className={`p-4 border-2 rounded-lg text-center ${
                bookingData.packageType === 'DOCUMENT'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Mail className="w-8 h-8 mx-auto mb-2" />
              <div className="font-medium">Document</div>
              <div className="text-sm text-gray-600">Papers, letters, files</div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Weight</label>
            <div className="flex">
              <input
                type="number"
                min="0"
                step="0.1"
                value={bookingData.weight || ''}
                onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
                className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter weight"
                required
              />
              <select
                value={bookingData.weightUnit}
                onChange={(e) => handleInputChange('weightUnit', e.target.value)}
                className="p-3 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="KG">KG</option>
              </select>
            </div>
          </div>
        </div>

        {bookingData.packageType === 'PACKAGE' && (
          <div>
            <label className="block text-sm font-medium mb-2">Dimensions (Optional)</label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <input
                  type="number"
                  min="0"
                  value={bookingData.length || ''}
                  onChange={(e) => handleInputChange('length', parseFloat(e.target.value) || 0)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Length"
                />
              </div>
              <div>
                <input
                  type="number"
                  min="0"
                  value={bookingData.width || ''}
                  onChange={(e) => handleInputChange('width', parseFloat(e.target.value) || 0)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Width"
                />
              </div>
              <div>
                <input
                  type="number"
                  min="0"
                  value={bookingData.height || ''}
                  onChange={(e) => handleInputChange('height', parseFloat(e.target.value) || 0)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Height"
                />
              </div>
            </div>
            <div className="mt-2">
              <select
                value={bookingData.dimensionUnit}
                onChange={(e) => handleInputChange('dimensionUnit', e.target.value)}
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="CM">CM</option>
              </select>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Package Description</label>
          <textarea
            value={bookingData.packageDescription}
            onChange={(e) => handleInputChange('packageDescription', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Describe the contents of your package"
            rows={3}
            required
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <User className="w-5 h-5" />
          <span>Your Information</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Full Name</label>
          <input
            type="text"
            value={bookingData.customerName}
            onChange={(e) => handleInputChange('customerName', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your full name"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Email Address</label>
          <input
            type="email"
            value={bookingData.customerEmail}
            onChange={(e) => handleInputChange('customerEmail', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your email address"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Phone Number</label>
          <input
            type="tel"
            value={bookingData.customerPhone}
            onChange={(e) => handleInputChange('customerPhone', e.target.value)}
            className={`w-full p-3 border rounded-lg focus:ring-2 ${
              phoneErrors.customerPhone 
                ? 'border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Enter your phone number (e.g., +971501234567)"
            required
          />
          {phoneErrors.customerPhone && (
            <p className="text-red-500 text-sm mt-1">{phoneErrors.customerPhone}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calendar className="w-5 h-5" />
          <span>Pickup & Delivery Schedule</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Pickup Date</label>
          <DatePicker
            selected={bookingData.pickupDate}
            onChange={(date: Date | null) => handleInputChange('pickupDate', date)}
            minDate={getMinDate()}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select pickup date"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            wrapperClassName="w-full"
            calendarClassName="shadow-lg"
            required
          />
        </div>

        {bookingData.pickupDate && (
          <div>
            <label className="block text-sm font-medium mb-2">Pickup Time Slot</label>
            {bookingData.pickupDate && isToday(bookingData.pickupDate) && timeSlots.length > 0 && (
              <p className="text-sm text-blue-600 mb-3">
                📅 Showing only available time slots for today (after current time)
              </p>
            )}
            <div className="grid grid-cols-1 gap-3">
              {timeSlots.length > 0 ? (
                timeSlots.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => handleInputChange('timeSlot', slot.id)}
                    className={`p-4 border-2 rounded-lg text-left ${
                      bookingData.timeSlot === slot.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{slot.time}</div>
                        <div className="text-sm text-gray-600">{slot.description}</div>
                      </div>
                      <Clock className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-sm text-yellow-800">
                    No available time slots for today. Please select a future date.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Delivery Date</label>
          <DatePicker
            selected={bookingData.deliveryDate}
            onChange={(date: Date | null) => handleInputChange('deliveryDate', date)}
            minDate={bookingData.pickupDate || getMinDate()}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select delivery date"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            wrapperClassName="w-full"
            calendarClassName="shadow-lg"
            disabled={!bookingData.pickupDate}
            required
          />
          {!bookingData.pickupDate && (
            <p className="text-sm text-gray-500 mt-1">Please select a pickup date first</p>
          )}
        </div>

        {bookingData.deliveryDate && bookingData.timeSlot && (
          <div>
            <label className="block text-sm font-medium mb-2">Delivery Time Slot</label>
            {isSameDay(bookingData.pickupDate, bookingData.deliveryDate) && deliveryTimeSlots.length > 0 && (
              <p className="text-sm text-blue-600 mb-3">
                📦 Same-day delivery - showing slots after pickup time
              </p>
            )}
            {bookingData.deliveryDate && isToday(bookingData.deliveryDate) && !isSameDay(bookingData.pickupDate, bookingData.deliveryDate) && deliveryTimeSlots.length > 0 && (
              <p className="text-sm text-blue-600 mb-3">
                📅 Showing only available time slots for today (after current time)
              </p>
            )}
            <div className="grid grid-cols-1 gap-3">
              {deliveryTimeSlots.length > 0 ? (
                deliveryTimeSlots.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => handleInputChange('deliveryTimeSlot', slot.id)}
                    className={`p-4 border-2 rounded-lg text-left ${
                      bookingData.deliveryTimeSlot === slot.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{slot.time}</div>
                        <div className="text-sm text-gray-600">{slot.description}</div>
                      </div>
                      <Clock className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-sm text-yellow-800">
                    {isSameDay(bookingData.pickupDate, bookingData.deliveryDate) 
                      ? "No available delivery slots after the selected pickup time. Please choose a different date or pickup time."
                      : "No available delivery slots for the selected date. Please choose a different delivery date."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {bookingData.deliveryDate && !bookingData.timeSlot && (
          <p className="text-sm text-gray-500 mt-2">Please select a pickup time slot first</p>
        )}
      </CardContent>
    </Card>
  );

  const renderStep5 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>Booking Summary</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold mb-3">Delivery Details</h3>
          <div className="space-y-2 text-sm">
            <div><strong>From:</strong> {bookingData.pickupCity}, {bookingData.pickupCountry}</div>
            <div><strong>To:</strong> {bookingData.dropoffCity}, {bookingData.dropoffCountry}</div>
            <div><strong>Package:</strong> {bookingData.packageType} ({bookingData.weight} {bookingData.weightUnit})</div>
            <div><strong>Pickup:</strong> {bookingData.pickupDate ? bookingData.pickupDate.toLocaleDateString('en-GB') : 'Not selected'} ({timeSlots.find(s => s.id === bookingData.timeSlot)?.time})</div>
            <div><strong>Delivery:</strong> {bookingData.deliveryDate ? bookingData.deliveryDate.toLocaleDateString('en-GB') : 'Not selected'} ({deliveryTimeSlots.find(s => s.id === bookingData.deliveryTimeSlot)?.time})</div>
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Pricing Details</h3>
          
          <div className="space-y-4">
            {/* Package Summary */}
            <div className="space-y-2 pb-3 border-b border-blue-200">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-700">Weight:</span>
                <span className="font-medium">{bookingData.weight} {bookingData.weightUnit}</span>
              </div>
              
              {((bookingData.length || 0) > 0 || (bookingData.width || 0) > 0 || (bookingData.height || 0) > 0) && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Dimensions:</span>
                  <span className="font-medium">
                    {bookingData.length || 0} × {bookingData.width || 0} × {bookingData.height || 0} cm
                  </span>
                </div>
              )}
              
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-700">Route:</span>
                <span className="font-medium">{bookingData.pickupCountry} → {bookingData.dropoffCountry}</span>
              </div>
            </div>
            
            {/* Total Amount */}
            <div className="flex justify-between items-center pt-2">
              <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
              {calculatingPrice ? (
                <span className="text-2xl font-bold text-blue-600 flex items-center">
                  <svg className="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Calculating...
                </span>
              ) : (
                <span className="text-3xl font-bold text-blue-600">
                  {priceBreakdown.total > 0 ? priceBreakdown.total.toFixed(2) : '0.00'} AED
                </span>
              )}
            </div>
            
            <p className="text-xs text-gray-600 mt-2 pt-2 border-t border-blue-200">
              💰 Rate calculated based on weight, dimensions, origin & destination
            </p>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-start space-x-3">
            <CreditCard className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-green-800 mb-1">Next: Secure Payment</h4>
              <p className="text-sm text-green-700">
                After clicking "Proceed to Payment", your booking will be created and you'll be redirected to our secure N-Genius payment gateway to complete the transaction.
              </p>
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-500 text-center">
          <p>By proceeding, you agree to our terms and conditions. Your booking will be confirmed after successful payment.</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      <PageMeta 
        title="Book Delivery - Bullet Delivery"
        description="Book your delivery with Bullet - Fast, reliable, and affordable delivery service"
        keywords="book delivery, shipping, courier, package delivery, express delivery"
      />
      
      {/* Custom DatePicker Styles */}
      <style dangerouslySetInnerHTML={{ __html: datePickerStyles }} />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Book Your Delivery</h1>
            <p className="text-gray-600">Fast, reliable, and affordable delivery service</p>
          </div>

          {renderStepIndicator()}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2 order-2 lg:order-1">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}
            </div>
            
            {/* Price Preview Sidebar - Show from Step 1 onwards */}
            {currentStep >= 1 && currentStep < 5 && (
              <div className="lg:col-span-1 order-1 lg:order-2">
                <Card className="sticky top-4">
                  <CardHeader>
                    <CardTitle className="text-lg">Price Estimate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Package Details */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Weight:</span>
                          <span className="font-medium">
                            {bookingData.weight > 0 ? `${bookingData.weight} ${bookingData.weightUnit}` : 'Not set'}
                          </span>
                        </div>
                        
                        {((bookingData.length || 0) > 0 || (bookingData.width || 0) > 0 || (bookingData.height || 0) > 0) && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Dimensions:</span>
                            <span className="font-medium">
                              {bookingData.length || 0} × {bookingData.width || 0} × {bookingData.height || 0} cm
                            </span>
                          </div>
                        )}
                        
                        {bookingData.pickupCountry && bookingData.dropoffCountry && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Route:</span>
                            <span className="font-medium text-xs">
                              {bookingData.pickupCountry} → {bookingData.dropoffCountry}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Total Price */}
                      <div className="border-t pt-4">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-900">Estimated Total:</span>
                          {calculatingPrice ? (
                            <span className="text-xl font-bold text-blue-600 flex items-center">
                              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Calculating...
                            </span>
                          ) : (
                            <span className="text-2xl font-bold text-blue-600">
                              {priceBreakdown.total > 0 ? priceBreakdown.total.toFixed(2) : '0.00'} AED
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-800">
                        💡 Price updates automatically as you fill the form
                      </p>
                    </div>
                    
                    {currentStep === 1 && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">
                          📍 Select pickup and drop-off locations to see if international surcharge applies
                        </p>
                      </div>
                    )}
                    
                    {currentStep === 2 && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">
                          📦 Enter package weight to see accurate pricing
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button
              onClick={prevStep}
              disabled={currentStep === 1}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Previous</span>
            </Button>

            {currentStep < 5 ? (
              <Button
                onClick={nextStep}
                disabled={!validateStep(currentStep)}
                className="flex items-center space-x-2"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                <CreditCard className="w-4 h-4" />
                <span>{loading ? 'Creating booking & redirecting to payment...' : 'Proceed to Payment'}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BookDelivery;
